import getValue from './getValue';
import getTagValues from './getTagValues';

/**
 * Helper to conditionally set a property on an object
 */
function setIfExists(obj, key, value) {
  if (value) {
    obj[key] = value;
  }
}

/**
 * Get product dimensions from a node
 */
function getProductDimensions(node, dimensions) {
  const result = {};
  dimensions.forEach((dim) => {
    const value = getValue(
      node,
      `[data-tag-product-dimension${dim}]`,
      `product-dimension${dim}`
    );
    if (value) {
      result[`dimension${dim}`] = value;
    }
  });
  return result;
}

/**
 * Build product info from product node
 */
function buildProductInfo(product, node, dataType) {
  const productInfo = {};
  const currency = getValue(node, '[data-tag-product-currency]', 'product-currency');
  
  setIfExists(productInfo, 'name', getValue(product, '[data-tag-product-name]', 'product-name'));
  setIfExists(productInfo, 'id', getValue(product, '[data-tag-product-id]', 'product-id'));
  setIfExists(productInfo, 'category', getValue(product, '[data-tag-product-category]', 'product-category'));
  setIfExists(productInfo, 'brand', getValue(product, '[data-tag-product-brand]', 'product-brand'));
  setIfExists(productInfo, 'variant', getValue(product, '[data-tag-product-variant]', 'product-variant'));
  setIfExists(productInfo, 'position', getValue(product, '[data-tag-product-position]', 'product-position'));
  
  const productPrice = getValue(product, '[data-tag-product-price]', 'product-price');
  productInfo.price = productPrice || '0.00';
  
  const productList = getValue(product, '[data-tag-product-list]', 'product-list');
  
  // Handle product dimensions
  const dimensions = getProductDimensions(product, [35, 36, 37, 38, 39, 40, 48, 49, 50, 91, 152, 153, 154]);
  Object.assign(productInfo, dimensions);
  
  // Note: There's a bug in the original code - dimension50 was assigned to dimension49
  // and dimension154 was assigned to dimension3154. Keeping as is for compatibility.
  if (dimensions.dimension50) {
    productInfo.dimension49 = dimensions.dimension50;
  }
  if (dimensions.dimension154) {
    productInfo.dimension3154 = dimensions.dimension154;
    delete productInfo.dimension154;
  }
  
  const dimensioncd176 = getValue(product, '[data-tag-product-dimensioncd176]', 'product-dimensioncd176');
  setIfExists(productInfo, 'dimensioncd176', dimensioncd176);
  
  return { productInfo, currency, productList };
}

/**
 * Handle product-related data types
 */
function handleProductData(node, dataFromDom, dataType) {
  const productsNodes = node.querySelectorAll('[data-tag-product-identifier]');
  dataFromDom.products = [];

  for (const product of productsNodes) {
    const { productInfo, currency, productList } = buildProductInfo(product, node, dataType);
    
    if (currency) {
      dataFromDom.currency = currency;
    }
    
    if (productList && dataType === 'productClick') {
      dataFromDom.list = productList;
    }
    if (productList && dataType === 'productImpression') {
      productInfo.list = productList;
    }
    if (productList && dataType === 'productVariantClick') {
      dataFromDom.list = productList;
    }

    dataFromDom.products.push(productInfo);
  }
}

/**
 * Handle promotion-related data types
 */
function handlePromotionData(node, dataFromDom) {
  const promotionNodes = node.querySelectorAll('[data-tag-promotion-identifier]');
  dataFromDom.promotions = [];

  for (const promotion of promotionNodes) {
    const promotionInfo = {};
    setIfExists(promotionInfo, 'name', getValue(promotion, '[data-tag-promotion-name]', 'promotion-name'));
    setIfExists(promotionInfo, 'id', getValue(promotion, '[data-tag-promotion-id]', 'promotion-id'));
    setIfExists(promotionInfo, 'creative', getValue(promotion, '[data-tag-promotion-creative]', 'promotion-creative'));
    setIfExists(promotionInfo, 'position', getValue(promotion, '[data-tag-promotion-position]', 'promotion-position'));
    dataFromDom.promotions.push(promotionInfo);
  }
}

/**
 * Handle add to cart data
 */
function handleAddToCart(node, dataFromDom, eventName) {
  const productInfo = {};
  
  setIfExists(productInfo, 'name', getValue(node, '[data-tag-product-name]', 'product-name'));
  setIfExists(productInfo, 'id', getValue(node, '[data-tag-product-id]', 'product-id'));
  setIfExists(productInfo, 'brand', getValue(node, '[data-tag-product-brand]', 'product-brand'));
  setIfExists(productInfo, 'category', getValue(node, '[data-tag-product-category]', 'product-category'));
  setIfExists(productInfo, 'variant', getValue(node, '[data-tag-product-variant]', 'product-variant'));
  setIfExists(productInfo, 'quantity', getValue(node, '[data-tag-product-quantity]', 'product-quantity'));
  
  const productPrice = getValue(node, '[data-tag-product-price]', 'product-price');
  productInfo.price = productPrice || 'none';
  
  // Add dimensions
  const dimensions = getProductDimensions(node, [36, 38, 48, 49, 59, 91]);
  Object.assign(productInfo, dimensions);
  
  const dimensioncd176 = getValue(node, '[data-tag-product-dimensioncd176]', 'product-dimensioncd176');
  setIfExists(productInfo, 'dimensioncd176', dimensioncd176);
  
  // Handle special dimension49 logic
  if (productInfo.dimension49) {
    const handBuypageURL = new URLSearchParams(window.location.search);
    if (handBuypageURL.has('utm_medium')) {
      productInfo.dimension49 = 'activated';
    }
  }

  const ecommerce = { add: { products: [productInfo] } };
  const currency = getValue(node, '[data-tag-product-currency]', 'product-currency');
  setIfExists(ecommerce, 'currencyCode', currency);
  
  dataFromDom.ecommerce = ecommerce;
  dataFromDom.ename = eventName;
  dataFromDom.productInfo = getValue(node, '[data-tag-product-info]', 'product-info');
  dataFromDom.storeName = getValue(node, '[data-tag-store-name]', 'store-name');
  dataFromDom.step = getValue(node, '[data-tag-step]', 'step');
}

/**
 * Handle social interaction event data
 */
function handleSocialIntEvent(node, dataFromDom, eventName) {
  dataFromDom.ename = eventName;
  dataFromDom.ecommerce = getValue(node, '[data-tag-ecommerce]', 'ecommerce');
  dataFromDom.network = getValue(node, '[data-tag-network]', 'network');
  dataFromDom.social_network = getValue(node, '[data-tag-social-network]', 'social-network');
  dataFromDom.social_action = getValue(node, '[data-tag-social-action]', 'social-action');
  dataFromDom.target = window.location.href;
}

/**
 * Handle user action event data
 */
function handleUserActionEvent(node, dataFromDom, eventName) {
  if (eventName) {
    dataFromDom.ename = eventName;
    dataFromDom.event_name = eventName;
  }
  
  setIfExists(dataFromDom, 'click_area', getValue(node, '[data-tag-click-area]', 'click-area'));
  setIfExists(dataFromDom, 'event_detail', getValue(node, '[data-tag-event-detail]', 'event-detail'));
  setIfExists(dataFromDom, 'breadcrumb', getValue(node, '[data-tag-breadcrumb]', 'breadcrumb'));
  setIfExists(dataFromDom, 'productInfo', getValue(node, '[data-tag-product-info]', 'product-info'));
  setIfExists(dataFromDom, 'step', getValue(node, '[data-tag-step]', 'step'));
  setIfExists(dataFromDom, 'article_name', getValue(node, '[data-tag-article-name]', 'article-name'));
  setIfExists(dataFromDom, 'module_name', getValue(node, '[data-tag-module-name]', 'module-name'));
  setIfExists(dataFromDom, 'cta_name', getValue(node, '[data-tag-cta-name]', 'cta-name'));
  setIfExists(dataFromDom, 'link_url', getValue(node, '[data-tag-link-url]', 'link-url'));
  setIfExists(dataFromDom, 'service_name', getValue(node, '[data-tag-service-name]', 'service-name'));
  setIfExists(dataFromDom, 'service_category', getValue(node, '[data-tag-service-category]', 'service-category'));
  setIfExists(dataFromDom, 'referral', getValue(node, '[data-tag-referral]', 'referral'));
}

/**
 * Returns all data it can find in the dom for an analytics action.
 * @param {Node} node Parent Node to query for data. Closest [data-tag-wrapper] to Node which triggered the event.
 * @param {Object} data Base data send by the Node which triggered the event. This data will be merged with the data we
 *   find in the Dom.
 */
export default function createDataObjectBasedOnDom(node, data) {
  const dataFromDom = getTagValues(node, [
    'category',
    'action',
    'label',
    'currency',
    'name',
  ]);
  const eventName = getValue(node, '[data-tag-event-name]', 'event-name');
  const productTypes = ['productClick', 'productImpression', 'productVariantClick'];
  const promotionTypes = ['promotionClick', 'promotionImpression'];

  if (productTypes.includes(data.type)) {
    handleProductData(node, dataFromDom, data.type);
  }

  if (promotionTypes.includes(data.type)) {
    handlePromotionData(node, dataFromDom);
  }

  if (data.type === 'addToCart') {
    handleAddToCart(node, dataFromDom, eventName);
  }

  if (data.type === 'socialIntEvent') {
    handleSocialIntEvent(node, dataFromDom, eventName);
  }

  if (data.type === 'userActionEvent') {
    handleUserActionEvent(node, dataFromDom, eventName);
  }

  return { ...data, ...dataFromDom };
}

